import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BreadcrumbComponent } from '@app/core/breadcrumb/breadcrumb.component';
import { HeaderComponent } from '@app/core/header/header.component';
import {
  advancedSearch,
  arrowDown,
  arrowH,
  arrowLeft,
  arrowRight,
  arrowUp,
  arrowV,
  bars,
  briefcase,
  bullhorn,
  calendar,
  calendarSelected,
  calendarViewDay,
  calendarViewMonth,
  calendarViewWeek,
  caretDown,
  caretLeft,
  caretRight,
  caretUp,
  chat,
  check,
  checkCircle,
  checkCircleFill,
  checkSquare,
  chevronDown,
  chevronLeft,
  chevronRight,
  chevronUp,
  circle,
  clock,
  close,
  closeCircle,
  cloudUpload,
  contrast,
  cookieBite,
  copy,
  download,
  edit,
  envelope,
  error,
  errorFill,
  eyeClosed,
  eyeOpen,
  file,
  fileAlt,
  fileDownload,
  fileSignature,
  filters,
  graduationCap,
  history,
  inbox,
  info,
  infoFill,
  kolumny,
  konsultacja,
  konsultacjaStacjonarna,
  korekta,
  legitymacja,
  link,
  linked,
  listaFinanse,
  magicWand,
  map,
  mapSigns,
  minus,
  more,
  moreText,
  move,
  naliczOplate,
  paperPlane,
  pause,
  pin,
  pinAlt,
  planZajec,
  plus,
  poll,
  print,
  przeczytane,
  przedloz,
  przeksiegowanie,
  przypiszWplate,
  question,
  redo,
  ribbon,
  rozlicz,
  save,
  search,
  settings,
  signIn,
  signOut,
  sort,
  star,
  tasks,
  th,
  trash,
  umorzenie,
  umowaEdit,
  unlinked,
  user,
  userAdd,
  userAnonim,
  userCircle,
  userEdit,
  userGroup,
  userList,
  userRemove,
  videoconference,
  videoconferenceJoin,
  warning,
  warningFill,
  windowClose,
  wykladowcy,
  wypelnijWniosek,
  wypozyczenia,
  wyslij,
  zalacznik,
  zaplac,
  zaplacOnline,
  zoomIn,
  zoomOut,
  zwrot,
} from '@components/icon/icons';
import { IconsRegistryService } from '@components/icon/icons-registry.service';
import { NotificationService } from '@components/notification/notification.service';
import { environment } from '@env/environment';
import { UstawieniaUzytkownikaService } from '@services/api/administracja/systems/ustawienia-uzytkownika.service';
import { UsersService } from '@services/api/administracja/user-access/users.service';
import { AuthService } from '@services/auth.service';
import { DomainErrorService } from '@services/domain-error.service';
import { LoaderService } from '@services/loader.service';
import { SystemDataService } from '@services/system-data.service';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { filter, forkJoin, of, take, tap } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AcceptLanguageHeaderInterceptor } from './core/accept-language-header.interceptor';
import { FooterComponent } from './core/footer/footer.component';
import { ModulesMenuComponent } from './core/header/modules-menu/modules-menu.component';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { LoaderInterceptor } from './core/interceptors/loader.interceptor';
import { LoaderComponent } from './core/loader/loader.component';
import { MenuComponent } from './core/menu/menu.component';
import { SlubowanieStudentaGuardModalComponent } from './core/menu/slubowanie-studenta-guard-modal/slubowanie-studenta-guard-modal.component';
import { ModulesListComponent } from './core/modules-list/modules-list.component';
import { SharedModule } from './shared/shared.module';

const httpInterceptors: Provider[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AcceptLanguageHeaderInterceptor,
    multi: true,
    deps: [LOCALE_ID, UstawieniaUzytkownikaService],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpErrorInterceptor,
    multi: true,
    deps: [DomainErrorService, NotificationService],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: LoaderInterceptor,
    multi: true,
    deps: [LoaderService],
  },
];

function initAppFactory(
  authService: AuthService,
  ustawieniaUzytkownikaService: UstawieniaUzytkownikaService,
  permissionsService: UsersService,
  systemDataService: SystemDataService,
) {
  return (): Promise<void> =>
    new Promise((resolve) => {
      const appInitObs = {
        authentication: authService.canActivateProtectedRoutes$.pipe(
          filter((v) => !!v),
          take(1),
          tap(() => {
            permissionsService.loadPermissions();
            ustawieniaUzytkownikaService.initUstawienia();
            systemDataService.getData();
          }),
        ),
        permissions: permissionsService.permissions$.pipe(
          filter((v) => !!v.length),
          take(1),
        ),
        userSettings:
          environment.production || environment.angularConfiguration === 'testing'
            ? ustawieniaUzytkownikaService.ustawieniaUzytkownika.pipe(
                filter((v) => !!v),
                take(1),
              )
            : of(null),
      };

      authService.loadDiscoveryDocumentAndLogin();

      forkJoin(appInitObs).subscribe(({ userSettings }) => {
        if (userSettings) {
          const appLang = window.location.pathname.split('/').at(1);
          const userLang = userSettings.jezykKod.split('-').at(0);

          if (appLang !== userLang) {
            // below we're assumming user had pasted link with another locale (if there's any path), so we're using href,
            // otherwise user would be redirected to page saved in sso requested route, which may vary
            const segmentedLocationPath = window.location.pathname.split('/');
            let destination: string;
            if (segmentedLocationPath.length > 2) {
              segmentedLocationPath.splice(0, 2); // delete empty string before starting slash and language code
              destination = `${window.location.origin}/${userLang}/${segmentedLocationPath.join('/')}${
                window.location.search
              }`;
            } else {
              const segmentedSsoReqRoute = localStorage.getItem('requested_route').split('/');
              segmentedSsoReqRoute.splice(0, 2); // delete empty string before starting slash and language code
              destination = `${window.location.origin}/${userLang}/${segmentedSsoReqRoute.join('/')}`;
            }
            window.location.href = destination;
          } else {
            resolve();
          }
        } else {
          resolve();
        }
      });
    });
}

function storageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({
  declarations: [
    AppComponent,
    LoaderComponent,
    MenuComponent,
    HeaderComponent,
    BreadcrumbComponent,
    FooterComponent,
    ModulesListComponent,
    ModulesMenuComponent,
    SlubowanieStudentaGuardModalComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    SharedModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiUrl],
        sendAccessToken: true,
      },
    }),
  ],
  providers: [
    ...httpInterceptors,
    { provide: OAuthStorage, useFactory: storageFactory },
    {
      provide: APP_INITIALIZER,
      useFactory: initAppFactory,
      multi: true,
      deps: [AuthService, UstawieniaUzytkownikaService, UsersService, SystemDataService],
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'PLN', multi: true },
    DatePipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private readonly iconsRegistry: IconsRegistryService) {
    this.iconsRegistry.registerIcons([
      advancedSearch,
      arrowDown,
      arrowH,
      arrowLeft,
      arrowRight,
      arrowUp,
      arrowV,
      bars,
      briefcase,
      bullhorn,
      calendar,
      calendarViewDay,
      calendarViewMonth,
      calendarViewWeek,
      calendarSelected,
      caretDown,
      caretLeft,
      caretRight,
      caretUp,
      chat,
      check,
      checkCircle,
      checkCircleFill,
      checkSquare,
      chevronDown,
      chevronUp,
      chevronLeft,
      chevronRight,
      circle,
      clock,
      close,
      closeCircle,
      cloudUpload,
      cookieBite,
      copy,
      contrast,
      download,
      edit,
      envelope,
      error,
      errorFill,
      eyeClosed,
      eyeOpen,
      fileAlt,
      fileDownload,
      fileSignature,
      file,
      filters,
      graduationCap,
      history,
      inbox,
      info,
      infoFill,
      kolumny,
      konsultacja,
      konsultacjaStacjonarna,
      korekta,
      legitymacja,
      link,
      linked,
      listaFinanse,
      magicWand,
      map,
      mapSigns,
      minus,
      more,
      moreText,
      move,
      naliczOplate,
      paperPlane,
      pinAlt,
      pin,
      planZajec,
      plus,
      poll,
      print,
      przeczytane,
      przedloz,
      przeksiegowanie,
      przypiszWplate,
      question,
      redo,
      ribbon,
      rozlicz,
      save,
      search,
      settings,
      signIn,
      signOut,
      sort,
      star,
      tasks,
      th,
      trash,
      umorzenie,
      umowaEdit,
      unlinked,
      user,
      userCircle,
      userAdd,
      userAnonim,
      userGroup,
      userList,
      userRemove,
      userEdit,
      videoconference,
      videoconferenceJoin,
      warning,
      warningFill,
      windowClose,
      wykladowcy,
      wypelnijWniosek,
      wypozyczenia,
      wyslij,
      zalacznik,
      zaplac,
      zoomIn,
      zoomOut,
      zwrot,
      pause,
      zaplacOnline,
    ]);
  }
}
